import { useEffect, useRef } from "react";
import { Box, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";

type Particle = {
	x: number;
	y: number;
	vx: number;
	vy: number;
	r: number;
	pulse: number;
	pulseSpeed: number;
};

const prefersReducedMotion = () =>
	typeof window !== "undefined"
	&& window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * App-wide fixed viewport backdrop: drifting plasma orbs, constellation
 * particles, and a soft scan grid. Theme-aware; static fallback for reduced motion.
 * Mount once at the app root so gaps / login / empty shells all share it.
 */
const AppLiveBackground = () => {
	const theme = useTheme();
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const isDark = theme.palette.mode === "dark";
	const primary = theme.palette.primary.main;
	const secondary = theme.palette.secondary?.main || "#deb111";

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas || prefersReducedMotion()) return;

		const ctx = canvas.getContext("2d", { alpha: true });
		if (!ctx) return;

		let raf = 0;
		let running = true;
		let width = 0;
		let height = 0;
		let dpr = 1;
		let particles: Particle[] = [];
		let t = 0;

		const linkDist = () => Math.min(160, Math.max(90, Math.min(width, height) * 0.14));

		const particleCount = () => {
			const area = width * height;
			if (area < 500_000) return 36;
			if (area < 1_200_000) return 56;
			return 72;
		};

		const spawn = (): Particle => ({
			x: Math.random() * width,
			y: Math.random() * height,
			vx: (Math.random() - 0.5) * 0.45,
			vy: (Math.random() - 0.5) * 0.45,
			r: 1.1 + Math.random() * 2.2,
			pulse: Math.random() * Math.PI * 2,
			pulseSpeed: 0.012 + Math.random() * 0.02,
		});

		const resize = () => {
			width = window.innerWidth;
			height = window.innerHeight;
			dpr = Math.min(window.devicePixelRatio || 1, 2);
			canvas.width = Math.floor(width * dpr);
			canvas.height = Math.floor(height * dpr);
			canvas.style.width = `${width}px`;
			canvas.style.height = `${height}px`;
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

			const count = particleCount();
			if (particles.length === 0) {
				particles = Array.from({ length: count }, spawn);
			} else if (particles.length < count) {
				while (particles.length < count) particles.push(spawn());
			} else if (particles.length > count) {
				particles.length = count;
			}
		};

		const hexToRgb = (hex: string): [number, number, number] => {
			const h = hex.replace("#", "");
			const full = h.length === 3
				? h.split("").map((c) => c + c).join("")
				: h;
			const n = parseInt(full.slice(0, 6), 16);
			if (Number.isNaN(n)) return isDark ? [239, 83, 80] : [139, 16, 16];
			return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
		};

		const [pr, pg, pb] = hexToRgb(primary);
		const [sr, sg, sb] = hexToRgb(secondary);

		const drawGrid = () => {
			const step = 56;
			const drift = (t * 8) % step;
			ctx.save();
			ctx.strokeStyle = isDark
				? `rgba(${pr},${pg},${pb},0.07)`
				: `rgba(${pr},${pg},${pb},0.06)`;
			ctx.lineWidth = 1;
			ctx.beginPath();
			for (let x = -step + drift; x < width + step; x += step) {
				ctx.moveTo(x, 0);
				ctx.lineTo(x, height);
			}
			for (let y = -step + drift * 0.6; y < height + step; y += step) {
				ctx.moveTo(0, y);
				ctx.lineTo(width, y);
			}
			ctx.stroke();
			ctx.restore();
		};

		const draw = () => {
			if (!running) return;
			t += 1;
			ctx.clearRect(0, 0, width, height);

			drawGrid();

			const maxLink = linkDist();

			// links
			for (let i = 0; i < particles.length; i++) {
				const a = particles[i];
				for (let j = i + 1; j < particles.length; j++) {
					const b = particles[j];
					const dx = a.x - b.x;
					const dy = a.y - b.y;
					const dist = Math.hypot(dx, dy);
					if (dist > maxLink) continue;
					const strength = 1 - dist / maxLink;
					ctx.strokeStyle = isDark
						? `rgba(${pr},${pg},${pb},${0.08 + strength * 0.28})`
						: `rgba(${pr},${pg},${pb},${0.05 + strength * 0.2})`;
					ctx.lineWidth = 0.6 + strength * 0.9;
					ctx.beginPath();
					ctx.moveTo(a.x, a.y);
					ctx.lineTo(b.x, b.y);
					ctx.stroke();
				}
			}

			// particles
			for (const p of particles) {
				p.x += p.vx;
				p.y += p.vy;
				p.pulse += p.pulseSpeed;

				if (p.x < -20) p.x = width + 20;
				if (p.x > width + 20) p.x = -20;
				if (p.y < -20) p.y = height + 20;
				if (p.y > height + 20) p.y = -20;

				const glow = 0.55 + 0.45 * Math.sin(p.pulse);
				const radius = p.r * (0.85 + glow * 0.35);

				const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius * 5);
				g.addColorStop(0, `rgba(${sr},${sg},${sb},${0.22 * glow})`);
				g.addColorStop(0.35, `rgba(${pr},${pg},${pb},${0.18 * glow})`);
				g.addColorStop(1, "transparent");
				ctx.fillStyle = g;
				ctx.beginPath();
				ctx.arc(p.x, p.y, radius * 5, 0, Math.PI * 2);
				ctx.fill();

				ctx.fillStyle = isDark
					? `rgba(255,255,255,${0.55 + glow * 0.35})`
					: `rgba(${pr},${pg},${pb},${0.55 + glow * 0.35})`;
				ctx.beginPath();
				ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
				ctx.fill();
			}

			raf = requestAnimationFrame(draw);
		};

		const onVisibility = () => {
			if (document.hidden) {
				running = false;
				cancelAnimationFrame(raf);
			} else if (!running) {
				running = true;
				raf = requestAnimationFrame(draw);
			}
		};

		resize();
		window.addEventListener("resize", resize);
		document.addEventListener("visibilitychange", onVisibility);
		raf = requestAnimationFrame(draw);

		return () => {
			running = false;
			cancelAnimationFrame(raf);
			window.removeEventListener("resize", resize);
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [isDark, primary, secondary]);

	return (
		<Box
			aria-hidden
			sx={{
				position: "fixed",
				inset: 0,
				width: "100vw",
				height: "100vh",
				overflow: "hidden",
				pointerEvents: "none",
				// Behind all app chrome; solid surfaces cover it where needed
				zIndex: 0,
				background: (t) =>
					t.palette.mode === "dark"
						? `radial-gradient(ellipse 80% 60% at 20% 10%, ${alpha(primary, 0.28)} 0%, transparent 55%),
						   radial-gradient(ellipse 70% 50% at 85% 75%, ${alpha(secondary, 0.12)} 0%, transparent 50%),
						   linear-gradient(165deg, #07090d 0%, #12151c 45%, #0a0c10 100%)`
						: `radial-gradient(ellipse 80% 60% at 15% 5%, ${alpha(primary, 0.16)} 0%, transparent 55%),
						   radial-gradient(ellipse 70% 50% at 90% 80%, ${alpha(secondary, 0.1)} 0%, transparent 50%),
						   linear-gradient(165deg, #f6f7fb 0%, #eef0f6 50%, #f8f9fc 100%)`,
			}}
		>
			{/* Plasma orbs */}
			<Box
				sx={{
					position: "absolute",
					width: "55vmax",
					height: "55vmax",
					top: "-18%",
					left: "-12%",
					borderRadius: "50%",
					background: (t) =>
						`radial-gradient(circle, ${alpha(primary, t.palette.mode === "dark" ? 0.45 : 0.28)} 0%, transparent 68%)`,
					filter: "blur(8px)",
					animation: "appLiveOrbA 18s ease-in-out infinite",
					"@media (prefers-reduced-motion: reduce)": {
						animation: "none",
					},
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					width: "48vmax",
					height: "48vmax",
					bottom: "-20%",
					right: "-10%",
					borderRadius: "50%",
					background: (t) =>
						`radial-gradient(circle, ${alpha(secondary, t.palette.mode === "dark" ? 0.22 : 0.16)} 0%, transparent 70%)`,
					filter: "blur(10px)",
					animation: "appLiveOrbB 22s ease-in-out infinite",
					"@media (prefers-reduced-motion: reduce)": {
						animation: "none",
					},
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					width: "36vmax",
					height: "36vmax",
					top: "40%",
					left: "55%",
					borderRadius: "50%",
					background: (t) =>
						`radial-gradient(circle, ${alpha(primary, t.palette.mode === "dark" ? 0.2 : 0.12)} 0%, transparent 72%)`,
					filter: "blur(12px)",
					animation: "appLiveOrbC 26s ease-in-out infinite",
					"@media (prefers-reduced-motion: reduce)": {
						animation: "none",
					},
				}}
			/>

			{/* Soft vignette */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					background: (t) =>
						t.palette.mode === "dark"
							? "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.55) 100%)"
							: "radial-gradient(ellipse at center, transparent 35%, rgba(255,255,255,0.35) 100%)",
				}}
			/>

			<canvas
				ref={canvasRef}
				style={{
					position: "absolute",
					inset: 0,
					width: "100%",
					height: "100%",
					display: "block",
				}}
			/>

			<style>{`
				@keyframes appLiveOrbA {
					0%, 100% { transform: translate(0, 0) scale(1); }
					50% { transform: translate(8%, 12%) scale(1.12); }
				}
				@keyframes appLiveOrbB {
					0%, 100% { transform: translate(0, 0) scale(1); }
					50% { transform: translate(-10%, -8%) scale(1.15); }
				}
				@keyframes appLiveOrbC {
					0%, 100% { transform: translate(0, 0) scale(1); }
					33% { transform: translate(-12%, 6%) scale(1.08); }
					66% { transform: translate(6%, -10%) scale(0.95); }
				}
			`}</style>
		</Box>
	);
};

export default AppLiveBackground;
